import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {ScreenSizeService} from "../services/screen-size.service";

type PageValue = number | '...';

@Component({
  selector: 'app-paginator',
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.css']
})
export class PaginatorComponent implements OnInit, OnChanges {

  @Input() totalItems!: number;
  @Input() pageSize: number = 10;
  @Input() currentPage: number = 1;
  @Input() maxVisiblePages: number = 3;
  @Input() isMobile!: boolean;

  pages: Array<PageValue> = [];

  @Output() pageChange:EventEmitter<number> = new EventEmitter<number>();

  constructor(
    private readonly screenSizeService: ScreenSizeService
  ) {
  }

  ngOnInit(): void {
    this.screenSizeService.isMobile$.subscribe({
      next: (isMobile: boolean) => {
        this.isMobile = isMobile;
      }
    });
  }

  ngOnChanges(_: SimpleChanges): void {
    this.updatePages();
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems / this.pageSize);
  }

  goToPage(page: PageValue): void {
    if (typeof page !== 'number' || page === this.currentPage) {
      return;
    }
    this.setCurrentPage(page);
  }

  previousPage(): void {
    if (this.currentPage <= 1) {
      return;
    }
    this.setCurrentPage(this.currentPage - 1);
  }

  nextPage(): void {
    if (this.currentPage >= this.totalPages) {
      return;
    }
    this.setCurrentPage(this.currentPage + 1);
  }

  private setCurrentPage(page: number): void {
    this.currentPage = page;
    this.updatePages();
    this.pageChange.emit(this.currentPage);
  }

  private updatePages(): void {
    this.pages = this.generatePages();
  }

  private generatePages(): Array<PageValue> {
    const total = this.totalPages;
    const current = this.currentPage;
    const visible = this.maxVisiblePages;

    if (total <= visible + 2) {
      return this.generateSimpleRange(total);
    }

    const pages: Array<PageValue> = [1];
    const {left, right} = this.calculatePageBounds(current, total, visible);

    if (left > 2) {
      pages.push('...');
    }
    pages.push(...this.generateRange(left, right));
    if (right < total - 1) {
      pages.push('...');
    }
    pages.push(total);

    return pages;
  }

  private generateSimpleRange(total: number): number[] {
    return Array.from({length: total}, (_, i) => i + 1);
  }

  private calculatePageBounds(current: number, total: number, visible: number): { left: number; right: number } {
    const half = Math.floor(visible / 2);
    let left = Math.max(current - half, 2);
    let right = Math.min(current + half, total - 1);

    if (current <= half + 2) {
      left = 2;
      right = visible + 1;
    } else if (current >= total - (half + 1)) {
      left = total - visible;
      right = total - 1;
    }

    return {left, right};
  }

  private generateRange(start: number, end: number): number[] {
    const range: number[] = [];
    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    return range;
  }
}
